import toPath from 'lodash/toPath'
import {useCallback, useMemo} from 'react'

import {useAppDispatch} from '../../../../hooks/react-redux'
import {restApi} from '../../../../services/rest'
import {BS} from '../../../../types/BS_types'
import {emptyMap} from '../../../../utils/empty'
import {storeDslFragment} from '../../Dsl/Dsl.actions'
import {defaultMatrixBuildParameter} from '../MatrixParamBuild.consts'
import type {MatrixParamBuildParameter} from '../MatrixParamBuild.types'

function useMatrixParamBuild(
  buildTypeId: string,
  projectId: string,
  featureId = '',
  featureType = '',
  readOnly?: string,
) {
  const dispatch = useAppDispatch()
  const isCreateMode = !featureId
  const {isBuildTypeMatrixLoading, dataParameters} = restApi.endpoints.getBuildTypeMatrix.useQuery(
    {btLocator: `id:${buildTypeId}`, featureId},
    {
      selectFromResult: ({currentData, isUninitialized, isLoading}) => ({
        dataParameters: currentData?.parameter ?? [defaultMatrixBuildParameter],
        isBuildTypeMatrixLoading: isLoading || isUninitialized,
      }),
    },
  )
  const [featureViewAsCode, {error: viewAsCodeError}] =
    restApi.endpoints.featureViewAsCode.useMutation()
  const [addMatrix, {error: addMatrixError}] = restApi.endpoints.addBuildTypeMatrix.useMutation()
  const [updateMatrix, {error: updateMatrixError}] =
    restApi.endpoints.updateBuildTypeMatrix.useMutation()
  const {projectsDefaultValueSets = [], isProjectsDefaultValueSetsLoading} =
    restApi.endpoints.getProjectsDefaultValueSets.useQuery(`id:${projectId}`, {
      selectFromResult: ({data, isLoading}) => ({
        isProjectsDefaultValueSetsLoading: isLoading,
        projectsDefaultValueSets: data?.valueSet,
      }),
    })
  const matrixParamBuildSubmitHandler = useCallback(
    async (parameters: MatrixParamBuildParameter[]) => {
      const params = {btLocator: buildTypeId, featureId, body: {parameter: parameters}}
      const result = isCreateMode ? await addMatrix(params) : await updateMatrix(params)

      if (!('error' in result)) {
        BS?.BuildFeatureDialog?.close()
        BS?.reload?.(true)
      }
    },
    [addMatrix, buildTypeId, featureId, isCreateMode, updateMatrix],
  )
  const matrixParamBuildViweAsCodeSubmitHandler = useCallback(
    async (parameters: MatrixParamBuildParameter[], version: string, portable: boolean) => {
      const matrixFeatureId = featureId || null
      const params = {
        btLocator: buildTypeId,
        featureId,
        body: {
          featureType,
          featureId: matrixFeatureId,
          showDSL: 'item',
          showDSLVersion: version,
          showDSLPortable: portable,
          descriptor: {
            parameter: parameters,
          },
        },
      }
      const result = await featureViewAsCode(params)

      if (!('error' in result)) {
        dispatch(storeDslFragment(result.data))
      } else {
        throw new Error('ivalide form')
      }
    },
    [buildTypeId, dispatch, featureId, featureType, featureViewAsCode],
  )
  const errorMessages = useMemo(() => {
    const errors = (viewAsCodeError || addMatrixError || updateMatrixError) as Record<
      string,
      string
    >[]

    if (errors?.length) {
      return errors.reduce<Map<string, string>>((accumulator, value) => {
        const {message, location} = value
        const [_, ...restPath] = toPath(location)
        const isErrorParameterSection = restPath.length === 1
        const errorPath = isErrorParameterSection ? [...restPath, 'name'] : restPath
        const pathField = errorPath
          .map(path => (!isNaN(Number(path)) ? `[${path}]` : path))
          .join('.')

        accumulator.set(pathField, message)

        return accumulator
      }, new Map())
    }

    return emptyMap
  }, [addMatrixError, updateMatrixError, viewAsCodeError])
  const isLoading = isProjectsDefaultValueSetsLoading || isBuildTypeMatrixLoading
  const isReadOnlyMode = readOnly === 'true'

  return {
    isLoading,
    errorMessages,
    isReadOnlyMode,
    data: dataParameters,
    projectsDefaultValueSets,
    matrixParamBuildSubmitHandler,
    matrixParamBuildViweAsCodeSubmitHandler,
  }
}

export default useMatrixParamBuild
